import { xhrFactory } from 'ethos-design-system/src/xhr/lib/xhr';
import { v4 as uuidv4 } from 'uuid';

import { OktaAuth } from '@okta/okta-auth-js';

import { ConfigService } from '../common/config.service';
import { SharedConstants } from '../common/constants/shared';
import { attempt } from '../common/utilities';
import { SnackBarType } from '../features/layout/interfaces/ISnacksProps';
import { Resource } from './constants/resource';
import { clientEnvVars } from './environment/clientEnvVars';
import { IKeyValMap } from './interfaces/IKeyValMap';
import { IXhrError } from './interfaces/IXhrError';

/**
 * Class to wrap XHR factory
 */
export class HttpService {
  private static instance: HttpService;
  private xhr: any;
  private baseURL: string = '';
  private oktaAuth: OktaAuth | null = null;

  private constructor() {
    this.baseURL = clientEnvVars().NORA_API_ROOT_URL;
    this.xhr = xhrFactory({ baseURL: this.baseURL });
  }

  public static getInstance() {
    if (!this.instance) {
      this.instance = new HttpService();
    }
    return this.instance;
  }

  public setOktaAuthService(oktaAuth: OktaAuth) {
    this.oktaAuth = oktaAuth;
  }

  private async handleResponse(
    response: any,
    err: IXhrError,
    path: string,
    method: string,
  ): Promise<IKeyValMap> {
    if (response) {
      return response;
    }

    let error;

    if (err?.response) {
      error = { ...err.response.parsedBody, id: uuidv4() };

      if (error.status === SharedConstants.FORBIDDEN) {
        error.message = error.message || Resource.FORBIDDEN_ERROR;
      } else if (
        error.status === SharedConstants.UNPROCESSABLE_ENTITY &&
        error.message.includes('invalid for transition')
      ) {
        // This error happens when eg someone tries to waive a task but
        // meanwhile task state has been updated in the backend so it can be no
        // longer waived. Solution would be to either refresh the UI or prompt
        // the user to refresh the page.
        error.message = Resource.INVALID_TRANSITION_ERROR;
        console.warn('Invalid transition', { error: err, path, method });
      } else {
        console.error('XHR error', { error: err, path, method });
      }
    } else {
      error = {
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message: Resource.NETWORK_ERROR,
      };

      // Do not log "Session not active" error as we already handle it by
      // logging out the user.
      if (err.message !== 'Session not active') {
        console.error('XHR error', { error: err, path, method });
      }
    }

    return { error };
  }

  /**
   * GET HTTP method
   * @param path
   */
  public async get(path: string): Promise<IKeyValMap> {
    const method = this.xhr.GET;
    const [headers, authErr] = await attempt(this.getHeaders());
    if (authErr) {
      return this.handleResponse(null, authErr, path, method);
    }

    const { err, response } = await this.xhr({
      path,
      method,
      credentials: 'include',
      headers,
    });
    return this.handleResponse(response, err, path, method);
  }

  /**
   * PATCH HTTP
   * @param path
   * @param body optional
   */
  public async patch(path: string, body: any = {}): Promise<IKeyValMap> {
    const method = this.xhr.PATCH;
    const [headers, authErr] = await attempt(this.getHeaders());
    if (authErr) {
      return this.handleResponse(null, authErr, path, method);
    }

    const { err, response } = await this.xhr({
      path,
      method,
      credentials: 'include',
      headers,
      body,
    });
    return this.handleResponse(response, err, path, method);
  }

  /**
   * POST HTTP method
   * @param path
   * @param body optional
   */
  public async post(path: string, body: any = {}): Promise<IKeyValMap> {
    const method = this.xhr.POST;

    const [headers, authErr] = await attempt(this.getHeaders());
    if (authErr) {
      return this.handleResponse(null, authErr, path, method);
    }

    const { err, response } = await this.xhr({
      path,
      method,
      credentials: 'include',
      headers,
      body,
    });
    return this.handleResponse(response, err, path, method);
  }

  /**
   * PUT HTTP method
   * @param path
   * @param body optional
   */
  public async put(path: string, body: any = {}): Promise<IKeyValMap> {
    const method = this.xhr.PUT;

    const [headers, authErr] = await attempt(this.getHeaders());
    if (authErr) {
      return this.handleResponse(null, authErr, path, method);
    }

    const { err, response } = await this.xhr({
      path,
      method,
      credentials: 'include',
      headers,
      body,
    });
    return this.handleResponse(response, err, path, method);
  }

  /**
   * DELETE HTTP method
   * @param path
   */
  public async delete(path: string): Promise<IKeyValMap> {
    const method = this.xhr.DELETE;

    const [headers, authErr] = await attempt(this.getHeaders());
    if (authErr) {
      return this.handleResponse(null, authErr, path, method);
    }

    const { err, response } = await this.xhr({
      path,
      method,
      credentials: 'include',
      headers,
    });
    return this.handleResponse(response, err, path, method);
  }

  public async postFiles(path: string, formData: any): Promise<IKeyValMap> {
    // const { err, response } = await this.xhr({
    //   path,
    //   method: this.xhr.POST,

    //   credentials: 'include',
    //   headers: {
    //     'Authorization': 'Bearer ' + Cookies.get(ACCESS_TOKEN_KEY)
    //   },
    //   body:formData,
    // });
    // return this.handleResponse(response, err);
    const method = 'POST';
    const response = await fetch(this.baseURL + path, {
      method,
      body: formData,
      credentials: 'include',
      headers: await this.getHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === SharedConstants.FORBIDDEN) {
        return Promise.reject({ statusCode: SharedConstants.FORBIDDEN });
      }

      // get error message from body or default to response status
      const error = (data && data.message) || response.status;
      console.error('fetch error', { error, path, method });
      return Promise.reject(error);
    }
    return data;
  }

  private async getHeaders() {
    const headers = {} as any;

    if (this.oktaAuth) {
      const accessToken = this.oktaAuth.getAccessToken();
      if (!accessToken) {
        // If for whatever reason we cannnot obtain token we should log the user
        // out. This could happen because underlying okta session expired or due
        // to https://github.com/okta/okta-oidc-js/issues/744
        console.warn('Cannot refresh the session');
        this.oktaAuth.signOut({
          postLogoutRedirectUri: ConfigService.getInstance().getLogoutUrl(),
        });
        throw new Error('Session not active');
      }

      headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
  }
}
